/**
 * Flat-rate (day rate) earnings for one pay assignment, as the pay screen
 * prices a row.
 *
 * MIRRORS the API's `src/lib/flat-rate-pay.ts`, which payroll.service.ts and
 * pay-ai.service.ts both call. Two repos, no shared package, so this is a
 * second copy by construction and not by choice; `flat-rate-pay.test.ts` runs
 * the API's own fixtures against it so the two cannot drift without a red
 * test. The place this copy becomes deletable is the API: if POST/PATCH
 * /tickets/:id/pay-assignments costed a day_rate row itself instead of storing
 * `calculatedAmount` verbatim, this screen would display and never compute.
 *
 * THE RULE, in one line: `subsistence` is billed PER HAND PER DAY, so its
 * `quantity` is person-days and each hand on the line is owed
 * `rate × quantity ÷ (hands named on the line)`. Every other flat type pays the
 * hand's own full day rate once, whatever the quantity says — see the gate
 * warning on FLAT_RATE_ONLY_LINE_ITEM_TYPES before widening it.
 *
 * FT-04415: 12 person-days at $250, four hands named — each owed 3 × $69 =
 * $207. Priced flat this screen wrote $69, and the operator's workaround was
 * typing 3 × 69 into the rate box by hand.
 *
 * ONE DELIBERATE DIVERGENCE FROM THE API. The API helper treats crew 0 as a
 * crew of 1 ("a caller bug; treat the assignment as its own crew"), and
 * payroll falls back further to the count of approved assignments. This screen
 * REFUSES instead: `{ status: 'no_crew' }`, the amount stays absent, and a
 * notice says so. The divisor is a fact about the line — how many hand-days
 * the customer was billed — and a line that names nobody has not said it.
 * Dividing 3 person-days by an invented 1 pays one hand for three days on the
 * office's say-so alone; FT-04419 is that exact line, and the $207 on it is a
 * human's arithmetic, not the system's.
 *
 * NON-INTEGER DAYS ARE PAID, NOT ROUNDED, and `isWholeDays` is returned so the
 * caller can flag the split for a human rather than move money silently.
 */
import { isPersonDayQuantityType } from '../constants/line-item-types';
import type { TechSlot } from './tech-slots';

export type FlatRateEarnings =
  | {
      status: 'priced';
      /** Dollars for this ONE assignment, rounded to cents. */
      earnings: number;
      /** Days this assignment is being paid for. 1 for every non-person-day type. */
      daysEach: number;
      /** The line's person-day count, or 1 where quantity is not person-days. */
      personDays: number;
      /** False when the crew does not divide the person-days evenly. */
      isWholeDays: boolean;
      /** True when quantity was consulted — the type is gated in. */
      usesPersonDays: boolean;
      /** The divisor used. 1 for every non-person-day type. */
      crew: number;
    }
  | {
      /** A person-day type on a line that names nobody: no share can be worked out. */
      status: 'no_crew';
      personDays: number;
    };

/**
 * How many hands are named ON THE LINE — the person-day divisor.
 *
 * Same rule as the API's `crewNamedOnLine`: a slot counts by its id when it
 * has one, otherwise by its initials, and the two are de-duplicated together.
 * NOT `techSlotsOf(li).length`: TNT's FT-04403 carries `CS` in two slots, and
 * the API counts that as one, so this must too or the screen and payroll
 * divide the same line by different numbers.
 */
export function crewNamedOnLine(slots: readonly TechSlot[]): number {
  const seen = new Set<string>();
  for (const s of slots) {
    if (s.id) seen.add(`id:${s.id}`);
    else if (s.initials) seen.add(`in:${s.initials.trim().toUpperCase()}`);
  }
  return seen.size;
}

export function flatRateEarnings(args: {
  lineItemType: string | null | undefined;
  quantity: unknown;
  crewNamed: number;
  /** Numerics arrive as strings from the API; `Number('69.00')` is 69. */
  dayRateAmount: unknown;
}): FlatRateEarnings {
  const rate = Number(args.dayRateAmount);
  const usesPersonDays = isPersonDayQuantityType(args.lineItemType);

  if (!usesPersonDays) {
    // crt / thread_wash / day_rate / standby: the hand's own full day rate,
    // once, regardless of quantity or crew size.
    return {
      status: 'priced',
      earnings: Math.round(rate * 100) / 100,
      daysEach: 1,
      personDays: 1,
      isWholeDays: true,
      usesPersonDays: false,
      crew: 1,
    };
  }

  // Numerics arrive as strings; `Number('12.0000')` is 12. A missing or
  // non-positive quantity is one day — the same reading as the API — never
  // zero days: a subsistence line that exists is a hand who ate.
  const q = Number(args.quantity);
  const personDays = Number.isFinite(q) && q > 0 ? q : 1;

  const crew = Number.isFinite(args.crewNamed) && args.crewNamed > 0 ? Math.floor(args.crewNamed) : 0;
  if (crew === 0) return { status: 'no_crew', personDays };

  const daysEach = personDays / crew;
  return {
    status: 'priced',
    earnings: Math.round(rate * daysEach * 100) / 100,
    daysEach,
    personDays,
    isWholeDays: Number.isInteger(daysEach),
    usesPersonDays: true,
    crew,
  };
}
